import axios from 'axios';

const client = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '/api',
    timeout: 15000,
});

export const normalizePaginatedResponse = (payload) => {
    const body = payload?.data ?? payload;

    if (Array.isArray(body)) {
        return {
            rows: body,
            total: body.length,
            limit: body.length,
            offset: 0,
        };
    }

    const rows = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body?.rows)
            ? body.rows
            : [];

    return {
        rows,
        total: Number(body?.total ?? rows.length ?? 0),
        limit: Number(body?.limit ?? rows.length ?? 0),
        offset: Number(body?.offset ?? 0),
    };
};

client.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

client.interceptors.response.use(
    (response) => response,
    (error) => {
        const code = error?.response?.data?.code;
        if (code === 'PASSWORD_CHANGE_REQUIRED' && window.location.pathname !== '/admin/force-password-change') {
            window.location.assign('/admin/force-password-change');
        }
        if (code === 'STORE_DISABLED') {
            // Clear session and redirect to login with reason
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (window.location.pathname !== '/admin/login') {
                window.location.assign('/admin/login?reason=store_disabled');
            }
        }
        return Promise.reject(error);
    }
);

const getDownloadFilename = (headers, fallbackName) => {
    const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
    const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    if (!match || !match[1]) return fallbackName;
    return decodeURIComponent(match[1].replace(/"/g, '').trim());
};

const downloadBlobResponse = (response, fallbackName) => {
    if (!response || response.data === undefined || response.data === null) {
        throw new Error('Download response was empty');
    }

    const blob = response?.data instanceof Blob
        ? response.data
        : new Blob([response?.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getDownloadFilename(response?.headers, fallbackName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const authApi = {
    register: async (data) => {
        try {
            const { data: res } = await client.post('/auth/register', data);
            return res;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    login: async (data) => {
        try {
            const { data: res } = await client.post('/auth/login', data);
            return res;
        } catch (err) {
            return {
                success: false,
                error: err?.response?.data?.error || err.message,
                code: err?.response?.data?.code || null,
            };
        }
    },
};

export const productsApi = {
    list: async (params = {}) => {
        try {
            const { data } = await client.get('/products', { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    create: async (payload) => {
        try {
            const { data } = await client.post('/products', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    update: async (id, payload) => {
        try {
            const { data } = await client.put(`/products/${id}`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    lowStock: async () => {
        try {
            const { data } = await client.get('/products/stock/low-stock');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    expired: async () => {
        try {
            const { data } = await client.get('/products/stock/expired');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    barcodeImage: async (id) => {
        try {
            const { data } = await client.get(`/products/${id}/barcode-image`);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    qrCode: async (id) => {
        try {
            const { data } = await client.get(`/products/${id}/qr-code`);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const posApi = {
    createTransaction: async (payload) => {
        try {
            const { data } = await client.post('/pos/transactions', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    transactions: async (params = {}) => {
        try {
            const { data } = await client.get('/pos/transactions', { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    getTransaction: async (transactionId) => {
        try {
            const { data } = await client.get(`/pos/transactions/${transactionId}`);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    dailyReport: async (params = {}) => {
        try {
            const { data } = await client.get('/pos/reports/daily', { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    searchCustomers: async (q) => {
        try {
            const { data } = await client.get('/pos/customers/search', { params: { q } });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    createCustomer: async (payload) => {
        try {
            const { data } = await client.post('/pos/customers', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    getLoyaltyConfig: async () => {
        try {
            const { data } = await client.get('/pos/loyalty-config');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    updateLoyaltyConfig: async (payload) => {
        try {
            const { data } = await client.put('/pos/loyalty-config', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    importPurchaseReceipt: async (file) => {
        try {
            const formData = new FormData();
            formData.append('receipt', file);
            const { data } = await client.post('/pos/import-purchase-receipt', formData);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const inventoryApi = {
    adjustStock: async (payload) => {
        try {
            const { data } = await client.post('/inventory/stock-adjustment', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    transferStock: async (payload) => {
        try {
            const { data } = await client.post('/inventory/stock-transfer', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    movements: async (params = {}) => {
        try {
            const { data } = await client.get('/inventory/movements', { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    autoReorderSuggestions: async () => {
        try {
            const { data } = await client.get('/inventory/suggestions/auto-reorder');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const storesApi = {
    list: async (params = {}) => {
        try {
            const { data } = await client.get('/stores', { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    create: async (payload) => {
        try {
            const { data } = await client.post('/stores', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    get: async (id) => {
        try {
            const { data } = await client.get(`/stores/${id}`);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    update: async (id, payload) => {
        try {
            const { data } = await client.put(`/stores/${id}`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const paymentConfigApi = {
    getStoreConfig: async (storeId) => {
        try {
            const { data } = await client.get(`/payment-config/stores/${storeId}/payment-config`);
            return { success: true, data };
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    setStoreFrontend: async (storeId, enabled) => {
        try {
            const { data } = await client.patch(`/payment-config/stores/${storeId}/payment-config/frontend`, { enabled });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    updatePaystack: async (storeId, payload) => {
        try {
            const { data } = await client.patch(`/payment-config/stores/${storeId}/payment-config/paystack`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    updateFlutterwave: async (storeId, payload) => {
        try {
            const { data } = await client.patch(`/payment-config/stores/${storeId}/payment-config/flutterwave`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    updateBankTransfer: async (storeId, payload) => {
        try {
            const { data } = await client.patch(`/payment-config/stores/${storeId}/payment-config/bank-transfer`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const inventoryMetaApi = {
    list: async (type) => {
        try {
            const { data } = await client.get(`/inventory/meta/${type}`);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    options: async () => {
        try {
            const { data } = await client.get('/inventory/meta/options');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    create: async (type, payload) => {
        try {
            const { data } = await client.post(`/inventory/meta/${type}`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    update: async (type, id, payload) => {
        try {
            const { data } = await client.put(`/inventory/meta/${type}/${id}`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    remove: async (type, id) => {
        try {
            const { data } = await client.delete(`/inventory/meta/${type}/${id}`);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const dashboardApi = {
    stats: async () => {
        try {
            const { data } = await client.get('/dashboard');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const tenantsApi = {
    me: async () => {
        try {
            const { data } = await client.get('/tenants/me');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    updateMe: async (payload) => {
        try {
            const { data } = await client.put('/tenants/me', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const usersApi = {
    list: async (params = {}) => {
        try {
            const { data } = await client.get('/users', { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    me: async () => {
        try {
            const { data } = await client.get('/users/me');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    create: async (payload) => {
        try {
            const { data } = await client.post('/users', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    update: async (id, payload) => {
        try {
            const { data } = await client.put(`/users/${id}`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    changePassword: async (payload) => {
        try {
            const { data } = await client.patch('/users/me/password', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    remove: async (id) => {
        try {
            const { data } = await client.delete(`/users/${id}`);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const ordersApi = {
    listByStore: async (storeId, params = {}) => {
        try {
            const { data } = await client.get(`/orders/store/${storeId}`, { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    getById: async (orderId) => {
        try {
            const { data } = await client.get(`/orders/${orderId}`);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    markPaymentReceived: async (orderId, paymentReference = '') => {
        try {
            const { data } = await client.put(`/orders/${orderId}/payment-received`, {
                payment_reference: paymentReference || undefined,
            });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    updateStatus: async (orderId, status) => {
        try {
            const { data } = await client.put(`/orders/${orderId}/status`, { status });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const smtpSettingsApi = {
    getMySettings: async () => {
        try {
            const { data } = await client.get('/smtp-settings/me');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    saveMySettings: async (payload) => {
        try {
            const { data } = await client.put('/smtp-settings/me', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    sendTestEmail: async (to) => {
        try {
            const { data } = await client.post('/smtp-settings/test', { to });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const licencesApi = {
    list: async (params = {}) => {
        try {
            const { data } = await client.get('/admin/licences', { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    issue: async (payload) => {
        try {
            const { data } = await client.post('/admin/licences/issue', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    extend: async (payload) => {
        try {
            const { data } = await client.post('/admin/licences/extend', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    reissue: async (payload) => {
        try {
            const { data } = await client.post('/admin/licences/reissue', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    setStatus: async (payload) => {
        try {
            const { data } = await client.post('/admin/licences/status', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    getDistribution: async () => {
        try {
            const { data } = await client.get('/admin/licences/distribution');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    downloadInstaller: async (kind = 'linux') => {
        const endpoint = kind === 'windows'
            ? '/admin/licences/download/install-ps1'
            : '/admin/licences/download/install-sh';
        const fallbackName = kind === 'windows' ? 'install.ps1' : 'install.sh';

        try {
            const response = await client.get(endpoint, { responseType: 'blob' });
            downloadBlobResponse(response, fallbackName);
            return { success: true };
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    downloadLatestPackage: async () => {
        try {
            const response = await client.get('/admin/licences/download/package/latest', { responseType: 'blob' });
            downloadBlobResponse(response, 'royaltechpos-store-package.tar.gz');
            return { success: true };
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};

export const masterAdminApi = {
    listStores: async (params = {}) => {
        try {
            const { data } = await client.get('/admin/master/stores', { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    createStore: async (payload) => {
        try {
            const { data } = await client.post('/admin/master/stores', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    updateStoreStatus: async (tenantId, status) => {
        try {
            const { data } = await client.patch(`/admin/master/stores/${tenantId}/status`, { status });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    updateStoreProfile: async (tenantId, payload) => {
        try {
            const { data } = await client.patch(`/admin/master/stores/${tenantId}`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    monitorStores: async (params = {}) => {
        try {
            const { data } = await client.get('/admin/master/stores/monitor', { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    resetStoreAdminPassword: async (tenantId, payload = {}) => {
        try {
            const { data } = await client.post(`/admin/master/stores/${tenantId}/reset-admin-password`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    updateStoreSubscription: async (tenantId, payload) => {
        try {
            const { data } = await client.patch(`/admin/master/stores/${tenantId}/subscription`, payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    billingOverview: async () => {
        try {
            const { data } = await client.get('/admin/master/billing/overview');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    billingHistory: async (params = {}) => {
        try {
            const { data } = await client.get('/admin/master/billing/history', { params });
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    getInstallerSecurity: async () => {
        try {
            const { data } = await client.get('/admin/master/installer/security');
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    rotateInstallerDistributionKey: async (payload = {}) => {
        try {
            const { data } = await client.post('/admin/master/installer/distribution-key/rotate', payload);
            return data;
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    downloadInstaller: async (kind = 'linux') => {
        const endpoint = kind === 'windows'
            ? '/admin/licences/download/install-ps1'
            : '/admin/licences/download/install-sh';
        const fallbackName = kind === 'windows' ? 'install.ps1' : 'install.sh';

        try {
            const response = await client.get(endpoint, { responseType: 'blob' });
            downloadBlobResponse(response, fallbackName);
            return { success: true };
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
    downloadLatestStorePackage: async () => {
        try {
            const response = await client.get('/admin/licences/download/package/latest', { responseType: 'blob' });
            downloadBlobResponse(response, 'royaltechpos-store-package.tar.gz');
            return { success: true };
        } catch (err) {
            return { success: false, error: err?.response?.data?.error || err.message };
        }
    },
};
